
import { GeolocationOptions } from './types';
import { getGeolocationErrorMessage } from './errors';
import { isSSR } from '../isSSR';
import { toast } from 'sonner';

// Check if geolocation is supported by the browser
export const isGeolocationSupported = (): boolean => {
  // In SSR, geolocation is never available
  if (isSSR()) {
    return false;
  }
  
  return !!navigator && !!navigator.geolocation;
};

// Function to get the user's current position with improved error handling
export const getCurrentPosition = (options: GeolocationOptions = {}): Promise<GeolocationPosition> => {
  const {
    enableHighAccuracy = true, 
    timeout = 30000, // Increased from 20000 to 30000
    maximumAge = 120000,
    onSuccess,
    onError,
    showSuccessToast = false
  } = options;
  
  return new Promise((resolve, reject) => {
    if (!isGeolocationSupported()) {
      const error = new Error("La geolocalizzazione non è supportata dal tuo browser");
      if (onError) onError(error as unknown as GeolocationPositionError);
      reject(error);
      return;
    }
    
    // Create a timeout ID for manual handling
    let timeoutId: number | null = null;
    
    // Define success handler
    const handleSuccess = (position: GeolocationPosition) => {
      // Clear timeout if it exists
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      
      console.log(`Position received: ${position.coords.latitude.toFixed(6)}, ${position.coords.longitude.toFixed(6)} (accuracy: ${Math.round(position.coords.accuracy)}m)`);
      
      if (showSuccessToast) {
        toast.success("Posizione rilevata con successo");
      }
      
      if (onSuccess) onSuccess(position);
      resolve(position);
    };
    
    // Define error handler
    const handleError = (error: GeolocationPositionError) => {
      // Clear timeout if it exists
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      
      const formattedError = getGeolocationErrorMessage(error);
      console.error("Errore geolocalizzazione:", formattedError, "Codice:", error.code);
      
      // Show error toast with retry button only for specific errors
      if (error.code === 3 && showSuccessToast) { // Only show for timeout errors when toast is enabled
        toast.error("Timeout nel rilevamento della posizione", {
          description: "Verifica la connessione e riprova",
          action: {
            label: "Riprova",
            onClick: () => getCurrentPosition({...options, enableHighAccuracy: false, showSuccessToast: true}),
          },
          duration: 5000,
        });
      }
      
      if (onError) onError(error);
      reject(error);
    };
    
    // Check for low-power mode or poor signal
    const connection = (navigator as any).connection;
    const lowPowerMode = (navigator as any).powerSavingEnabled;
    
    // Adjust accuracy based on device conditions
    let finalEnableHighAccuracy = enableHighAccuracy;
    if (connection && (connection.saveData || connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g')) {
      // If on slow connection, use lower accuracy to improve speed
      finalEnableHighAccuracy = false;
      console.log("Slow connection detected, using lower accuracy for geolocation");
    }
    
    if (lowPowerMode) {
      // If in power saving mode, use lower accuracy
      finalEnableHighAccuracy = false;
      console.log("Power saving mode detected, using lower accuracy for geolocation");
    }
    
    // Log attempt
    console.log(`Requesting position (highAccuracy: ${finalEnableHighAccuracy}, timeout: ${timeout}ms, maximumAge: ${maximumAge}ms)`);
    
    try {
      // Start geolocation request
      navigator.geolocation.getCurrentPosition(
        handleSuccess,
        handleError,
        { enableHighAccuracy: finalEnableHighAccuracy, timeout, maximumAge }
      );
      
      // Set a manual timeout as a fallback - slightly longer than the browser timeout
      timeoutId = window.setTimeout(() => {
        // Create a custom timeout error that matches GeolocationPositionError structure
        const timeoutError = {
          code: 3,
          message: "Timeout manuale nel rilevamento della posizione",
          PERMISSION_DENIED: 1,
          POSITION_UNAVAILABLE: 2,
          TIMEOUT: 3
        } as GeolocationPositionError;
        
        handleError(timeoutError);
      }, timeout + 5000); // Give extra time compared to browser timeout
    } catch (e) {
      console.error("Unexpected error requesting geolocation:", e);
      reject(e);
    }
  });
};
